import { useState, useEffect } from 'react';
import { useChallengeStorage } from './useChallengeStorage';

export const usePromptChallenge = (language, title, navigate) => {
  const { canAttemptChallenge, saveChallenge, isChallengeCompleted } = useChallengeStorage();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const [originalTask,       setOriginalTask]       = useState(null);
  const [promptChallenge,    setPromptChallenge]    = useState(null);
  const [constraintKey,      setConstraintKey]      = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [description,        setDescription]        = useState('');
  const [topic,              setTopic]              = useState('');
  const [userPrompt,         setUserPrompt]         = useState('');
  const [idealPrompt,        setIdealPrompt]        = useState('');
  const [score,              setScore]              = useState(null);
  const [issues,             setIssues]             = useState([]);
  const [breakdown,          setBreakdown]          = useState(null);
  const [submissionHistory,  setSubmissionHistory]  = useState([]);
  const [isSubmitting,       setIsSubmitting]       = useState(false);
  const [hasAccess,          setHasAccess]          = useState(false);
  const [accessError,        setAccessError]        = useState(null);

  // Single access check on mount — no ref hack needed because we're not
  // putting canAttemptChallenge in the dependency array; we call it once
  // and store the result in state.
  useEffect(() => {
    if (!language || !title) return;

    const accessCheck = canAttemptChallenge();

    if (!accessCheck.allowed) {
      setHasAccess(false);
      setAccessError(accessCheck.reason);
      setLoading(false);
      return;
    }

    setHasAccess(true);
    setAccessError(null);
    loadChallenge();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, title]);

  const loadChallenge = async () => {
    try {
      const challenges  = require('../challenges/promptEng.json');
      const decodedTitle = decodeURIComponent(title).replace(/-/g, ' ');
      const foundTask   = challenges.find(
        c => c.title.toLowerCase() === decodedTitle.toLowerCase()
      );

      if (!foundTask) { navigate('/challenges'); return; }

      setOriginalTask(foundTask);
      setDescription(foundTask.description || '');
      setTopic(foundTask.topic || '');
      await generateConstraints(foundTask);

    } catch {
      navigate('/challenges');
    }
  };

  const generateConstraints = async (task) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-prompt-constraints`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ challenge: task }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const data       = await response.json();
      const difficulty = task.difficulty?.toLowerCase() || 'medium';

      setPromptChallenge({
        objective:       `Write the most effective prompt that satisfies all test cases for: "${task.title}"`,
        constraints:     data.constraints,
        scoringCriteria: [
          'Rule Compliance (60%) - Satisfying all test case requirements',
          'Prompt Effectiveness (40%) - Clarity, specificity, and usability',
        ],
        difficulty:   data.difficulty || task.difficulty || 'Medium',
        minimumScore: data.minimumScore || getMinimumPassingScore(difficulty),
      });

      setConstraintKey(data.constraintKey);

    } catch {
      const difficulty = task.difficulty?.toLowerCase() || 'medium';
      const constraints = task.testCases?.length
        ? task.testCases
        : [`Complete the challenge: ${task.title}`];

      setPromptChallenge({
        objective:       `Write the most effective prompt that satisfies all test cases for: "${task.title}"`,
        constraints,
        scoringCriteria: [
          'Rule Compliance (60%) - Satisfying all test case requirements',
          'Prompt Effectiveness (40%) - Clarity, specificity, and usability',
        ],
        difficulty:   task.difficulty || 'Medium',
        minimumScore: getMinimumPassingScore(difficulty),
      });
      // constraintKey stays null — submit handler handles this case
    } finally {
      setLoading(false);
    }
  };

  const getMinimumPassingScore = (difficulty) => {
    const scores = { 'very easy': 65, 'easy': 70, 'medium': 75, 'hard': 80, 'expert': 85 };
    return scores[difficulty?.toLowerCase()] || 75;
  };

  const handlePromptSubmission = async () => {
    if (!userPrompt.trim() || isSubmitting || !hasAccess) return;

    if (!constraintKey) {
      setScore(null);
      setIssues([
        'Challenge constraints are still loading from the server.',
        'Please wait a moment and try again, or refresh the page if this persists.',
      ]);
      return;
    }

    setIsSubmitting(true);
    setIdealPrompt('');
    setScore(null);
    setIssues([]);
    setBreakdown(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/submit-solution`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ userInput: userPrompt, constraintKey }),
      });

      if (!response.ok) throw new Error(`Submission error: ${response.status}`);

      const data = await response.json();

      if (data.aiScoringFailed) {
        setIssues([
          ...(data.issues || []),
          '⚠️ AI scoring service was unavailable. Constraint score shown but effectiveness may be inaccurate. Please resubmit.',
        ]);
      }

      setIdealPrompt(data.aiResponse || 'No ideal prompt generated');
      setScore(data.finalScore);
      if (!data.aiScoringFailed) setIssues(data.issues || []);
      setBreakdown(data.breakdown || null);

      const newSubmission = {
        id:          Date.now(),
        prompt:      userPrompt,
        score:       data.finalScore,
        success:     data.success,
        idealPrompt: data.aiResponse || 'No ideal prompt generated',
        issues:      data.issues || [],
        message:     data.message,
        breakdown:   data.breakdown || null,
        details:     data.details  || null,
        timestamp:   new Date(),
      };

      setSubmissionHistory(prev => [newSubmission, ...prev]);

      if (data.success) {
        const alreadySolved = isChallengeCompleted(
          originalTask.language || language,
          originalTask.title
        );

        await saveChallenge({
          language:    originalTask.language || language,
          title:       originalTask.title,
          description: originalTask.description || description,
          topic:       originalTask.topic || topic,
          score:       data.finalScore,
          bestScore:   data.finalScore,
          isUpdate:    alreadySolved,
          attempts:    1,
          difficulty:  originalTask.difficulty || 'Medium',
          status:      data.finalScore >= (promptChallenge?.minimumScore || 75) ? 'solved' : 'attempted',
          objective:   promptChallenge.objective,
          constraints: promptChallenge.constraints,
          finalPrompt: userPrompt,
          idealPrompt: data.aiResponse,
          message:     data.message,
          breakdown:   data.breakdown,
          details:     data.details,
        });
      }

    } catch (error) {
      setScore(0);
      setIssues([
        `Submission failed: ${error.message}`,
        'Please check that the server is reachable and try again.',
      ]);
      setIdealPrompt('Unable to generate ideal prompt due to error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    originalTask,
    promptChallenge,
    constraintKey,
    loading,
    description,
    topic,
    userPrompt,
    setUserPrompt,
    idealPrompt,
    score,
    issues,
    breakdown,
    submissionHistory,
    isSubmitting,
    hasAccess,
    accessError,
    handlePromptSubmission,
  };
};